/**
 * Storage-level configuration contracts for the workflow-configuration
 * capability. No handler, registry, execution behavior, authorization
 * behavior, or hook-specific parameter schema accompanies these enums —
 * those belong to the transition-engine and administration changes that
 * consume this storage.
 */

/** The phase a workflow status belongs to. */
export enum WorkflowPhase {
	INTAKE = 'intake',
	PRODUCTION = 'production',
	VALIDATION = 'validation',
	DELIVERY = 'delivery',
}

/** Whether a transition hook runs before or after the transition it is attached to. */
export enum HookStage {
	PRE = 'pre',
	POST = 'post',
}

/**
 * The identity of a transition hook. `HookStage` and `HookCode` deliberately
 * list codes belonging to both stages in one enum; which codes are valid at
 * which stage is a rule the change that implements hook behavior owns.
 */
export enum HookCode {
	CHECKLIST_COMPLETE = 'checklist_complete',
	DELIVERY_SLOTS_FILLED = 'delivery_slots_filled',
	ARTIFACT_EXISTS = 'artifact_exists',
	CHANGE_REQUEST_CREATED = 'change_request_created',
	CHANGE_REQUEST_RESOLVED = 'change_request_resolved',
	ASSIGNEE_PRESENT = 'assignee_present',
	/** Passes only when the order has every field listed in its params (`RequiredWorkOrderField`). */
	FIELDS_PRESENT = 'fields_present',
	/** Passes only when the order has a draft design file, and activates the drafts in the move's transaction. */
	DESIGN_DRAFTS_SUBMITTED = 'design_drafts_submitted',
	EMIT_EVENT = 'emit_event',
	NOTIFY = 'notify',
	/**
	 * Diagnostic: writes a log line, optionally after a delay. Exists to prove a
	 * transition's post-hook wiring actually executes in an environment, which a
	 * hook with a real side effect cannot do without producing one.
	 */
	LOG_MESSAGE = 'log_message',
}

/** A work-order field a `fields_present` guard can require. */
export enum RequiredWorkOrderField {
	ASSET_TYPE = 'asset_type',
	COMPLEXITY = 'complexity',
	BRAND = 'brand',
	MARKET = 'market',
	DUE_DATE = 'due_date',
	/** A `channel` value: where the piece runs. */
	PLATFORM = 'platform',
	/** A main copy whose content is not blank. */
	MAIN_COPY = 'main_copy',
	/** No campaign, or a campaign with a media plan. */
	CAMPAIGN_MATERIALS = 'campaign_materials',
}

/**
 * The workspace a status's work is resolved into by default or by exact
 * asset-type override — one kind per stage of the confirmed ToBe mockup,
 * `queue_card` (Backlog) through `summary` (Done).
 */
export enum WorkspaceCode {
	QUEUE_CARD = 'queue_card',
	RESOLUTION = 'resolution',
	PRODUCTION = 'production',
	VALIDATION = 'validation',
	DISTRIBUTION = 'distribution',
	SUMMARY = 'summary',
}

/**
 * The section cards a workspace can be composed of. Sections are
 * host-agnostic: the same code renders the same component whichever
 * workspace lists it, so composition is authored per assignment in
 * `workspace_assignment.options` rather than hardcoded per workspace.
 * Change-request surfacing is workflow-driven and deliberately absent.
 */
export enum WorkspaceSectionCode {
	BRIEF_SUMMARY = 'brief_summary',
	GUIDELINES = 'guidelines',
	RELATED_CAMPAIGNS = 'related_campaigns',
	RELATED_DELIVERABLES = 'related_deliverables',
	APPLICABLE_RULES = 'applicable_rules',
	MESSAGES = 'messages',
	DELIVERY_SLOTS = 'delivery_slots',
	/**
	 * Configuring a generation — picking the prompt and filling its variables.
	 *
	 * Named for what the section IS rather than for the free-text field it used to
	 * be built around: the briefing is now one variable on a chosen prompt, not the
	 * section's whole purpose. `_CONTEXT` distinguishes setting a generation up from
	 * running one, which `IMAGE_GENERATION_REQUESTED` already means.
	 *
	 * Was `visual_brief` until 0045, colliding with the retired `visual_brief` AXIS
	 * on the same string — two different things named identically is what made the
	 * section look like classification when it never was.
	 */
	AI_GENERATION_CONTEXT = 'ai_generation_context',
	FINAL_DELIVERABLES = 'final_deliverables',
	VALIDATION_RUN = 'validation_run',
	VALIDATION_PDF = 'validation_pdf',
	AUDIT_LOG = 'audit_log',
	DISTRIBUTION_PACKAGE = 'distribution_package',
}

/** Whether a transition moves a work order forward or backward through its workflow. */
export enum TransitionDirection {
	FORWARD = 'forward',
	BACKWARD = 'backward',
}

/**
 * The categories workflow-configuration diagnostics can report. These codes
 * are the contract a client renders from — it never parses the human-readable
 * message to learn what a finding is. The set is closed: a new category is a
 * new code, never a widened meaning of an existing one.
 */
export enum WorkflowDiagnosticCode {
	/** The tenant has no active, non-soft-deleted default workflow. Tenant-level, and predicts total creation failure. */
	TENANT_DEFAULT_WORKFLOW_MISSING = 'TENANT_DEFAULT_WORKFLOW_MISSING',
	/** An active, non-terminal status has no active outbound transition, so work entering it cannot leave. */
	STATUS_WITHOUT_OUTBOUND_TRANSITION = 'STATUS_WITHOUT_OUTBOUND_TRANSITION',
	/** A non-soft-deleted work order carries no workflow association at all. */
	WORK_ORDER_WITHOUT_WORKFLOW = 'WORK_ORDER_WITHOUT_WORKFLOW',
	/** A work order's workflow is missing, inactive, soft-deleted, or owned by another tenant. */
	WORK_ORDER_INVALID_WORKFLOW = 'WORK_ORDER_INVALID_WORKFLOW',
	/** A work order's current status code does not exist, or is not active, in its assigned workflow. */
	WORK_ORDER_STATUS_NOT_AVAILABLE = 'WORK_ORDER_STATUS_NOT_AVAILABLE',
	/** An active resolver rule targets a missing, inactive, soft-deleted, or cross-tenant workflow. */
	RESOLVER_RULE_INVALID_WORKFLOW = 'RESOLVER_RULE_INVALID_WORKFLOW',
}

/**
 * The kind of resource a diagnostic finding points at. Tells a client which
 * identifier on the finding is meaningful without inferring it from the code.
 */
export enum WorkflowDiagnosticSubject {
	TENANT = 'tenant',
	WORKFLOW = 'workflow',
	WORKFLOW_STATUS = 'workflow_status',
	WORK_ORDER = 'work_order',
	RESOLVER_RULE = 'resolver_rule',
}

/**
 * The outcome of attempting to repair one work order. The set is closed and
 * exhaustive: every processed work order returns exactly one of these, and
 * only `REPAIRED` corresponds to a write.
 */
export enum WorkflowRepairOutcome {
	/** The workflow association was updated to the newly resolved workflow; the status was preserved unchanged. */
	REPAIRED = 'REPAIRED',
	/** The work order already referenced the resolved workflow and holds a valid status in it. Nothing was written. */
	NO_OP = 'NO_OP',
	/** The current status code is not an active status of the newly resolved workflow. Nothing was changed — never reset. */
	STATUS_NOT_AVAILABLE = 'STATUS_NOT_AVAILABLE',
	/** Resolution failed, context was unobtainable, or terminality could not be established safely. Nothing was changed. */
	UNRESOLVABLE = 'UNRESOLVABLE',
	/** The work order is terminal and out of scope for repair. Nothing was changed. */
	TERMINAL_SKIPPED = 'TERMINAL_SKIPPED',
}
